<template>
  <div class="minister-incidents">
    <PageLoader :storage="appLoading" />
    <ServerError v-if="serverError" />

    <div class="minister-incidents__header">
      <div class="header-left">
        <button @click="$router.push({ name: dashboardRouteName })" class="back-btn">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="back-btn__icon"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
          Back to Dashboard
        </button>
        <div class="minister-incidents__title-group">
          <div class="minister-incidents__eyebrow">{{ eyebrowLabel }}</div>
          <div class="minister-incidents__date">{{ previousDayLabel }}</div>
        </div>
      </div>
      <div class="header-right">
        <div class="incidents-counter">
          <span class="incidents-counter__label">Total Incidents</span>
          <span class="incidents-counter__value">{{ incidents.length }}</span>
        </div>
      </div>
    </div>

    <div class="minister-incidents__container">
      <!-- Audit / Totals Match Section -->
      <div class="minister-incidents__audit-card">
        <div class="audit-card__title">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" class="audit-card__icon"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
          Incident Count Verification Audit
        </div>
        <div class="audit-card__metrics">
          <div class="audit-metric">
            <span class="audit-metric__label">Total Incidents</span>
            <span class="audit-metric__value">{{ incidents.length }}</span>
          </div>
          <div class="audit-metric-divider"></div>
          <div class="audit-metric">
            <span class="audit-metric__label">District Sum</span>
            <span class="audit-metric__value">{{ districtTotalAll }}</span>
          </div>
          <div class="audit-metric-divider"></div>
          <div class="audit-metric">
            <span class="audit-metric__label">Division Sum</span>
            <span class="audit-metric__value">{{ divisionTotalAll }}</span>
          </div>
        </div>
      </div>

      <!-- Search & Filters -->
      <div class="table-actions">
        <div class="search-box">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="search-box__icon"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by animal, district, division..."
            class="search-box__input"
          />
        </div>
        <div class="filter-group">
          <select v-model="filterType" class="filter-select">
            <option value="all">All Types</option>
            <option value="HWC">HWC Only</option>
            <option value="Snake">Snake Only</option>
          </select>
          <select v-model="filterStatus" class="filter-select">
            <option value="all">All Statuses</option>
            <option value="resolved">Resolved / Rescued</option>
            <option value="pending">Pending / Assigned</option>
          </select>
        </div>
      </div>

      <!-- District & Division Summaries -->
      <div class="minister-incidents__summary-grid">
        <div class="summary-panel">
          <div class="summary-panel__header">
            <h3 class="summary-panel__title">District Summary ({{ uniqueDistrictsCount }} Mapped)</h3>
            <div class="summary-panel__totals">
              <span class="total-badge">Total: {{ districtTotalAll }}</span>
              <span class="total-badge total-badge--highlight">Map: {{ districtTotalKnown }}</span>
            </div>
          </div>
          <div class="summary-panel__content">
            <div v-for="item in districtSummary" :key="item.name" class="summary-pill">
              <span class="summary-pill__name">{{ item.name }}</span>
              <span class="summary-pill__count">{{ item.count }}</span>
            </div>
          </div>
        </div>
        <div class="summary-panel">
          <div class="summary-panel__header">
            <h3 class="summary-panel__title">Division Summary ({{ uniqueDivisionsCount }} Mapped)</h3>
            <div class="summary-panel__totals">
              <span class="total-badge">Total: {{ divisionTotalAll }}</span>
              <span class="total-badge total-badge--highlight">Mapped: {{ divisionTotalMapped }}</span>
            </div>
          </div>
          <div class="summary-panel__content">
            <div v-for="item in divisionSummary" :key="item.name" class="summary-pill">
              <span class="summary-pill__name">{{ item.name }}</span>
              <span class="summary-pill__count">{{ item.count }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Table Card -->
      <div class="table-card">
        <div class="table-wrapper">
          <table class="incidents-table">
            <thead>
              <tr>
                <th @click="sortBy('type')" class="sortable">
                  Type
                  <span class="sort-icon" v-if="sortKey === 'type'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('animalName')" class="sortable">
                  Animal Name
                  <span class="sort-icon" v-if="sortKey === 'animalName'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('count')" class="sortable">
                  Count
                  <span class="sort-icon" v-if="sortKey === 'count'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('district')" class="sortable">
                  District
                  <span class="sort-icon" v-if="sortKey === 'district'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('division')" class="sortable">
                  Division
                  <span class="sort-icon" v-if="sortKey === 'division'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('incidentDate')" class="sortable">
                  Incident Date
                  <span class="sort-icon" v-if="sortKey === 'incidentDate'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('incidentTime')" class="sortable">
                  Incident Time
                  <span class="sort-icon" v-if="sortKey === 'incidentTime'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('createdAt')" class="sortable">
                  Created At
                  <span class="sort-icon" v-if="sortKey === 'createdAt'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('reportedBy')" class="sortable">
                  Reported By
                  <span class="sort-icon" v-if="sortKey === 'reportedBy'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
                <th @click="sortBy('status')" class="sortable">
                  Status
                  <span class="sort-icon" v-if="sortKey === 'status'">{{ sortOrder === 'asc' ? '▲' : '▼' }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="incident in paginatedIncidents" :key="incident.id">
                <td>
                  <span :class="['type-badge', `type-badge--${incident.type.toLowerCase()}`]">
                    {{ incident.type }}
                  </span>
                </td>
                <td class="cell-primary">{{ incident.animalName }}</td>
                <td><strong>{{ incident.count || 1 }}</strong></td>
                <td>{{ incident.district }}</td>
                <td>{{ incident.division }}</td>
                <td>{{ formatDate(incident.incidentDate) }}</td>
                <td>{{ formatTime(incident.incidentTime) }}</td>
                <td>{{ formatDateTime(incident.createdAt) }}</td>
                <td>{{ incident.reportedBy }}</td>
                <td>
                  <span :class="['status-badge', `status-badge--${getStatusClass(incident.status)}`]">
                    {{ incident.status }}
                  </span>
                </td>
              </tr>
              <tr v-if="filteredIncidents.length === 0">
                <td colspan="10" class="empty-state">
                  <div class="empty-state__icon">🔍</div>
                  <div class="empty-state__text">No incidents found matching the criteria</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="pagination" v-if="filteredIncidents.length > 0">
          <div class="pagination__info">
            Showing {{ (currentPage - 1) * itemsPerPage + 1 }} - {{ Math.min(currentPage * itemsPerPage, filteredIncidents.length) }} of {{ filteredIncidents.length }} incidents
          </div>
          <div class="pagination__controls">
            <button
              :disabled="currentPage === 1"
              @click="currentPage--"
              class="pagination-btn"
            >
              Previous
            </button>
            <button
              v-for="page in totalPages"
              :key="page"
              :class="['pagination-btn', { 'pagination-btn--active': currentPage === page }]"
              @click="currentPage = page"
            >
              {{ page }}
            </button>
            <button
              :disabled="currentPage === totalPages"
              @click="currentPage++"
              class="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      appLoading: false,
      serverError: false,
      incidents: [],
      previousDayLabel: "Previous day",
      selectedDate: "",
      searchQuery: "",
      filterType: "all",
      filterStatus: "all",
      sortKey: "date",
      sortOrder: "desc",
      currentPage: 1,
      itemsPerPage: 10,
      resolvedStatuses: new Set([
        "resolved",
        "closed",
        "captured",
        "rescued",
        "released",
        "handover",
        "approved",
        "fraud"
      ])
    };
  },
  computed: {
    eyebrowLabel() {
      if (this.filterType === "Snake") return "Previous Day SARPA (Snake) Incidents";
      if (this.filterType === "HWC") return "Previous Day WildWatch (HWC) Incidents";
      return "Previous Day Incidents";
    },
    districtSummary() {
      const summaryMap = {};
      this.incidents.forEach((item) => {
        const dName = item.district || "Unknown";
        summaryMap[dName] = (summaryMap[dName] || 0) + 1;
      });
      return Object.entries(summaryMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    },
    divisionSummary() {
      const summaryMap = {};
      this.incidents.forEach((item) => {
        const dName = item.division || "-";
        summaryMap[dName] = (summaryMap[dName] || 0) + 1;
      });
      return Object.entries(summaryMap)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);
    },
    districtTotalAll() {
      return this.districtSummary.reduce((sum, item) => sum + item.count, 0);
    },
    districtTotalKnown() {
      return this.districtSummary
        .filter(item => item.name !== "Unknown" && item.name !== "undefined" && item.name !== "null")
        .reduce((sum, item) => sum + item.count, 0);
    },
    divisionTotalAll() {
      return this.divisionSummary.reduce((sum, item) => sum + item.count, 0);
    },
    divisionTotalMapped() {
      return this.divisionSummary
        .filter(item => item.name !== "-" && item.name !== "undefined" && item.name !== "null")
        .reduce((sum, item) => sum + item.count, 0);
    },
    isCountMatched() {
      const total = this.incidents.length;
      return this.districtTotalAll === total && this.divisionTotalAll === total;
    },
    uniqueDistrictsCount() {
      return this.districtSummary.filter(item => item.name !== "Unknown" && item.name !== "undefined" && item.name !== "null").length;
    },
    uniqueDivisionsCount() {
      return this.divisionSummary.filter(item => item.name !== "-" && item.name !== "undefined" && item.name !== "null").length;
    },
    dashboardRouteName() {
      return this.$route.name === "adminCommonIncidents"
        ? "commonanalyticspage"
        : "ministerDashboard";
    },
    unassignedRouteName() {
      return this.$route.name === "adminCommonIncidents"
        ? "adminCommonUnassigned"
        : "ministerUnassigned";
    },
    filteredIncidents() {
      return this.incidents
        .filter((item) => {
          // Type filter
          if (this.filterType !== "all" && item.type !== this.filterType) {
            return false;
          }

          // Status filter
          if (this.filterStatus !== "all") {
            const statusLower = (item.status || "").toLowerCase();
            const isResolved = this.resolvedStatuses.has(statusLower);
            if (this.filterStatus === "resolved" && !isResolved) return false;
            if (this.filterStatus === "pending" && isResolved) return false;
          }

          // Search query
          if (this.searchQuery) {
            const query = this.searchQuery.toLowerCase();
            const animal = (item.animalName || "").toLowerCase();
            const district = (item.district || "").toLowerCase();
            const division = (item.division || "").toLowerCase();
            const reporter = (item.reportedBy || "").toLowerCase();
            return animal.includes(query) || district.includes(query) || division.includes(query) || reporter.includes(query);
          }

          return true;
        })
        .sort((a, b) => {
          let fieldA = a[this.sortKey] || "";
          let fieldB = b[this.sortKey] || "";

          if (typeof fieldA === "string") fieldA = fieldA.toLowerCase();
          if (typeof fieldB === "string") fieldB = fieldB.toLowerCase();

          if (fieldA < fieldB) return this.sortOrder === "asc" ? -1 : 1;
          if (fieldA > fieldB) return this.sortOrder === "asc" ? 1 : -1;
          return 0;
        });
    },
    totalPages() {
      return Math.ceil(this.filteredIncidents.length / this.itemsPerPage) || 1;
    },
    paginatedIncidents() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredIncidents.slice(start, end);
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    filterType() {
      this.currentPage = 1;
    },
    filterStatus() {
      this.currentPage = 1;
    },
    $route() {
      this.syncFiltersFromRoute();
      this.loadIncidents();
    }
  },
  mounted() {
    this.syncFiltersFromRoute();
    this.loadIncidents();
  },
  methods: {
    syncFiltersFromRoute() {
      const routeStatus = typeof this.$route.query.status === "string"
        ? this.$route.query.status
        : "all";
      const routeDate = typeof this.$route.query.date === "string"
        ? this.$route.query.date
        : "";
      const routeType = typeof this.$route.query.type === "string"
        ? this.$route.query.type
        : (typeof this.$route.query.source === "string" ? this.$route.query.source : null);

      this.filterStatus = ["all", "resolved", "pending"].includes(routeStatus)
        ? routeStatus
        : "all";
      this.selectedDate = routeDate;

      if (routeType === "sarpa" || routeType === "snake" || routeType === "Snake") {
        this.filterType = "Snake";
      } else if (routeType === "wildwatch" || routeType === "hwc" || routeType === "HWC") {
        this.filterType = "HWC";
      } else if (routeType === "all") {
        this.filterType = "all";
      } else {
        // Default to HWC so snake reports are not mixed in when type query param is omitted
        this.filterType = "HWC";
      }
    },
    openUnassigned() {
      this.$router.push({
        name: this.unassignedRouteName,
        query: {
          date: this.selectedDate || undefined,
        },
      });
    },
    async loadIncidents() {
      this.appLoading = true;
      this.serverError = false;
      try {
        const apiType = this.filterType === "Snake" ? "sarpa" : (this.filterType === "HWC" ? "wildwatch" : (this.$route.query.type || undefined));
        const [summaryRes, listRes] = await Promise.all([
          axios({
            method: "GET",
            url: "/minister/dashboard/summary",
            headers: { "x-auth-token": localStorage.getItem("token") },
            params: {
              date: this.selectedDate || undefined,
              type: apiType,
            },
          }),
          axios({
            method: "GET",
            url: "/minister/dashboard/incidents",
            headers: { "x-auth-token": localStorage.getItem("token") },
            params: {
              date: this.selectedDate || undefined,
              type: apiType,
            },
          })
        ]);

        this.incidents = listRes.data.data || [];
        
        const summary = summaryRes.data.data;
        if (summary && summary.date) {
          this.previousDayLabel = new Date(summary.date).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
      } catch (error) {
        console.error(error);
        this.serverError = true;
      } finally {
        this.appLoading = false;
      }
    },
    getStatusClass(status) {
      if (!status) return "pending";
      const statusLower = status.toLowerCase();
      if (this.resolvedStatuses.has(statusLower)) {
        return "resolved";
      }
      if (statusLower === "pending" || statusLower === "assigned" || statusLower === "onreview") {
        return "pending";
      }
      return "neutral";
    },
    formatDate(value) {
      if (!value) return "-";
      if (typeof value === "string" && value.match(/^\d{2}-\d{2}-\d{4}$/)) return value;
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });
    },
    formatTime(value) {
      if (!value) return "-";
      if (typeof value === "string" && (value.includes("AM") || value.includes("PM"))) return value;
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    },
    formatDateTime(value) {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return String(value);
      return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
    }
  }
};
</script>

<style scoped>
.minister-incidents {
  height: auto;
  min-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  padding: 4px;
}

.minister-incidents__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 12px;
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.12);
  color: #2e5a38;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(18, 42, 24, 0.05);
  transition: all 0.2s ease;
}

.back-btn:hover {
  background: #f4f8f3;
  border-color: rgba(34, 64, 35, 0.24);
  transform: translateX(-2px);
}

.back-btn__icon {
  color: #2e5a38;
}

.minister-incidents__title-group {
  display: flex;
  flex-direction: column;
}

.minister-incidents__eyebrow {
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #697b69;
  font-weight: 700;
}

.minister-incidents__date {
  margin-top: 2px;
  font-size: 20px;
  line-height: 1.1;
  color: #183824;
  font-weight: 700;
}

.incidents-counter {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 18px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.08);
  box-shadow: 0 4px 12px rgba(18, 42, 24, 0.05);
}

.incidents-counter__label {
  font-size: 13px;
  color: #6c7c6c;
  font-weight: 500;
}

.incidents-counter__value {
  font-size: 18px;
  font-weight: 800;
  color: #173824;
}

.minister-incidents__container {
  flex: 1;
  min-height: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow: visible;
}

.table-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
  min-width: 280px;
}

.search-box__icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #7d907d;
  pointer-events: none;
}

.search-box__input {
  width: 100%;
  padding: 10px 14px 10px 42px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.12);
  color: #173824;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(18, 42, 24, 0.04);
  transition: all 0.2s ease;
}

.search-box__input:focus {
  outline: none;
  border-color: #2e5a38;
  box-shadow: 0 4px 16px rgba(46, 90, 56, 0.1);
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.filter-select {
  padding: 10px 16px;
  border-radius: 14px;
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.12);
  color: #173824;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(18, 42, 24, 0.04);
  outline: none;
  transition: all 0.2s ease;
}

.filter-select:focus {
  border-color: #2e5a38;
}

.table-card {
  flex: 1;
  min-height: 720px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(34, 64, 35, 0.08);
  border-radius: 20px;
  box-shadow: 0 16px 28px rgba(18, 42, 24, 0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.table-wrapper {
  flex: 1;
  min-height: 620px;
  max-height: calc(100vh - 340px);
  overflow-y: auto;
}

.incidents-table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.incidents-table th {
  position: sticky;
  top: 0;
  background: #f4f8f3;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  color: #4a5c4d;
  letter-spacing: 0.05em;
  border-bottom: 1px solid rgba(34, 64, 35, 0.08);
  z-index: 1;
  user-select: none;
}

.incidents-table th.sortable {
  cursor: pointer;
}

.incidents-table th.sortable:hover {
  color: #173824;
  background: #ecf3eb;
}

.sort-icon {
  margin-left: 6px;
  font-size: 10px;
}

.incidents-table td {
  padding: 16px 20px;
  font-size: 14px;
  color: #384c3c;
  border-bottom: 1px solid rgba(34, 64, 35, 0.05);
}

.incidents-table tbody tr {
  transition: background-color 0.15s ease;
}

.incidents-table tbody tr:hover {
  background-color: rgba(46, 90, 56, 0.03);
}

.cell-primary {
  font-weight: 700;
  color: #173824;
}

.type-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.type-badge--hwc {
  background: #e2f0d9;
  color: #385624;
}

.type-badge--snake {
  background: #e2eff9;
  color: #205c90;
}

.status-badge {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 700;
}

.status-badge--resolved {
  background: #e2f5e4;
  color: #2e6b36;
}

.status-badge--pending {
  background: #fff0db;
  color: #a36518;
}

.status-badge--neutral {
  background: #f0f0f0;
  color: #555555;
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-state__icon {
  font-size: 40px;
  margin-bottom: 12px;
}

.empty-state__text {
  font-size: 15px;
  color: #718171;
  font-weight: 500;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-top: 1px solid rgba(34, 64, 35, 0.08);
  background: #ffffff;
}

.pagination__info {
  font-size: 13px;
  color: #6c7c6c;
}

.pagination__controls {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pagination-btn {
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(34, 64, 35, 0.1);
  background: #ffffff;
  color: #2e5a38;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background: #f4f8f3;
  border-color: rgba(34, 64, 35, 0.2);
}

.pagination-btn--active {
  background: #2e5a38;
  border-color: #2e5a38;
  color: #ffffff;
  cursor: default;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #8c9c8c;
}

.minister-incidents__summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  margin-top: 4px;
}

.summary-panel {
  background: #ffffff;
  border: 1px solid rgba(34, 64, 35, 0.08);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 24px rgba(18, 42, 24, 0.04);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.minister-incidents__audit-card {
  background: linear-gradient(135deg, rgba(244, 248, 243, 0.9) 0%, rgba(230, 240, 228, 0.9) 100%);
  border: 1px solid rgba(46, 90, 56, 0.15);
  border-radius: 18px;
  padding: 16px 24px;
  box-shadow: 0 12px 24px rgba(18, 42, 24, 0.06);
  display: flex;
  flex-direction: column;
  gap: 12px;
  backdrop-filter: blur(10px);
}

.audit-card__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #173824;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.audit-card__icon {
  color: #2e5a38;
  flex-shrink: 0;
}

.audit-card__metrics {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
}

.audit-metric {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.audit-metric__label {
  font-size: 10px;
  text-transform: uppercase;
  color: #697b69;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.audit-metric__value {
  font-size: 20px;
  font-weight: 800;
  color: #173824;
  line-height: 1.2;
}

.audit-metric__sub {
  font-size: 11px;
  color: #5c6c5c;
  font-weight: 500;
}

.audit-link {
  color: #c62828 !important;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.audit-link:hover {
  opacity: 0.8;
}

.audit-metric-divider {
  width: 1px;
  height: 36px;
  background: rgba(34, 64, 35, 0.12);
  align-self: center;
}

.audit-metric__status {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background: #fdf2f2;
  color: #ec5959;
  border: 1px solid rgba(236, 89, 89, 0.2);
}

.audit-metric__status.status--matched {
  background: #e2f5e4;
  color: #2e6b36;
  border: 1px solid rgba(46, 107, 54, 0.2);
}

@media (max-width: 768px) {
  .minister-incidents {
    min-height: auto;
    overflow: visible;
  }

  .table-card {
    min-height: 560px;
  }

  .table-wrapper {
    min-height: 460px;
    max-height: none;
  }

  .audit-card__metrics {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .audit-metric-divider {
    display: none;
  }
}

.summary-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-panel__title {
  font-size: 13px;
  font-weight: 700;
  color: #173824;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.summary-panel__totals {
  display: flex;
  align-items: center;
  gap: 8px;
}

.total-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  background: #f0f4f0;
  border: 1px solid rgba(34, 64, 35, 0.1);
  color: #4a5c4d;
  border-radius: 8px;
}

.total-badge--highlight {
  background: #e2f0d9;
  border-color: rgba(46, 90, 56, 0.2);
  color: #2e5a38;
}

.summary-panel__content {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 120px;
  overflow-y: auto;
  padding-right: 4px;
}

.summary-pill {
  background: #f4f8f3;
  border: 1px solid rgba(46, 90, 56, 0.12);
  border-radius: 12px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #2e5a38;
  transition: all 0.2s ease;
}

.summary-pill:hover {
  background: #eef5ee;
  border-color: rgba(46, 90, 56, 0.24);
}

.summary-pill__count {
  background: #2e5a38;
  color: #ffffff;
  font-size: 10px;
  font-weight: 800;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .table-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    max-width: none;
  }
  
  .filter-group {
    justify-content: space-between;
  }
  
  .filter-select {
    flex: 1;
  }
}
</style>
